import { z } from 'zod';
import { createHash } from 'node:crypto';
import { platformCanonicalJson } from '../platform-canonical-json.contract.js';
import { platformStorageSha256Schema } from './platform-storage.contract.js';
/** Provider-neutral handoff authority for a later, separately authorized object transfer. */
export const platformStorageImportHandoffCapability = 'platform.storage-import-handoff.v1';
export const platformStorageImportHandoffContractVersion = platformStorageImportHandoffCapability;
const uuid = z.string().uuid();
const timestamp = z.string().datetime({ offset: true });
const sourceHash = z.string().regex(/^[a-f0-9]{64}$/);
const sourceId = z.string().trim().min(1).max(500);
const bucket = z.string().trim().regex(/^[a-z0-9][a-z0-9-]{1,62}$/);
const objectKey = z.string().trim().min(1).max(2_000).superRefine((value, context) => {
    if (/^[a-z][a-z0-9+.-]*:\/\//i.test(value) || /[\u0000-\u001f\u007f]/.test(value) || value.includes('\\') ||
        value.split('/').some((segment) => segment === '.' || segment === '..')) {
        context.addIssue({ code: 'custom', message: 'Source object key must be opaque metadata, not a URL or traversal path.' });
    }
});
const contentType = z.string().trim().min(1).max(255);
const byteCount = z.number().int().positive().max(Number.MAX_SAFE_INTEGER);
export const platformStorageImportHandoffSourceObjectIdentitySchema = z.strictObject({
    sourceBucket: bucket,
    sourceObjectKey: objectKey,
    sourceFileId: sourceId,
});
export const platformStorageImportHandoffExpectedContentSchema = z.strictObject({
    sha256: platformStorageSha256Schema,
    byteCount,
    contentType,
});
/** No target storage IDs are accepted here; they are issued by the privileged transfer authority. */
export const platformStorageImportHandoffRequestSchema = z.strictObject({
    contractVersion: z.literal(platformStorageImportHandoffContractVersion),
    tenantId: uuid,
    runId: uuid,
    sourceManifestSha256: sourceHash,
    sourceSha256: sourceHash,
    sourceUpdatedAt: timestamp.nullable(),
    sourceThrough: timestamp,
    targetProjectFileEvidenceId: uuid,
    sourceObject: platformStorageImportHandoffSourceObjectIdentitySchema,
    expected: platformStorageImportHandoffExpectedContentSchema,
});
/** Facts returned by the injected privileged transfer/readback port. */
export const platformStorageImportHandoffExecutorResultSchema = z.strictObject({
    tenantId: uuid,
    uploadIntentId: uuid,
    platformObjectId: uuid,
    actualSha256: platformStorageSha256Schema,
    actualByteCount: byteCount,
    actualContentType: contentType,
    verifiedAt: timestamp,
    attachedAt: timestamp,
    readbackVerified: z.boolean(),
});
export const platformStorageImportHandoffReceiptSchema = z.strictObject({
    contractVersion: z.literal(platformStorageImportHandoffContractVersion),
    receiptId: uuid,
    tenantId: uuid,
    runId: uuid,
    sourceManifestSha256: sourceHash,
    sourceSha256: sourceHash,
    sourceUpdatedAt: timestamp.nullable(),
    sourceThrough: timestamp,
    targetProjectFileEvidenceId: uuid,
    sourceObject: platformStorageImportHandoffSourceObjectIdentitySchema,
    expected: platformStorageImportHandoffExpectedContentSchema,
    binding: z.strictObject({ uploadIntentId: uuid, platformObjectId: uuid }),
    readback: z.strictObject({ actualSha256: platformStorageSha256Schema, actualByteCount: byteCount, actualContentType: contentType, verifiedAt: timestamp, attachedAt: timestamp, readbackVerified: z.literal(true) }),
    /** This is deliberately not a transfer-completed claim; the caller owns that lifecycle decision. */
    transferClaim: z.literal('not-claimed'),
    receiptSha256: sourceHash,
});
export const platformStorageImportTransferAttemptContractVersion = 'platform.storage-import-transfer-attempt.v1';
export const platformStorageImportTransferAttemptSchema = z.strictObject({
    contractVersion: z.literal(platformStorageImportTransferAttemptContractVersion),
    claimStatus: z.enum(['claimed', 'busy', 'verified', 'observed', 'transitioned']),
    attemptId: uuid,
    commandId: uuid,
    generation: z.number().int().min(1).max(1_000_000),
    uploadIntentId: uuid,
    platformObjectId: uuid,
    state: z.enum(['claimed', 'transferring', 'verified', 'quarantined', 'abandoned']),
    claimEpoch: z.number().int().min(1).max(1_000_000),
    leaseExpiresAt: timestamp.nullable(),
    failureCode: z.string().regex(/^[a-z][a-z0-9-]{0,79}$/).nullable(),
}).superRefine((value, context) => {
    const active = value.state === 'claimed' || value.state === 'transferring';
    if (active !== (value.leaseExpiresAt !== null)) {
        context.addIssue({ code: 'custom', path: ['leaseExpiresAt'], message: 'Transfer attempt lease does not match its state.' });
    }
    const failed = value.state === 'quarantined' || value.state === 'abandoned';
    if (failed !== (value.failureCode !== null)) {
        context.addIssue({ code: 'custom', path: ['failureCode'], message: 'Transfer attempt disposition does not match its state.' });
    }
});
export function platformStorageImportHandoffReceiptDigest(value) {
    return createHash('sha256').update(platformCanonicalJson(value), 'utf8').digest('hex');
}
export function platformStorageImportHandoffReceiptId(value) {
    const digest = createHash('sha256').update(platformCanonicalJson(value), 'utf8').digest('hex');
    const bytes = Buffer.from(digest.slice(0, 32), 'hex');
    bytes[6] = (bytes[6] & 0x0f) | 0x50;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = bytes.toString('hex');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
/** Exported for tests and adapters that need to prove the exact canonical input. */
export { platformCanonicalJson };
//# sourceMappingURL=platform-storage-import-handoff.contract.js.map